import React, { useEffect, useMemo, useState } from 'react';
import { SessionService } from '../services/sessionService';
import { API_URLS } from '../config/api';
import { CrawlDetail, PubDetails } from '../api/types';
import { getQueryParam } from '../utils/urlUtils';
import Sheet from './ui/Sheet';
import Markdown from './Markdown';
import { ChevronLeftIcon, ChevronRightIcon } from './ui/icons';
import './WelcomeSheet.css';

interface WelcomeSheetProps {
  isOpen: boolean;
  onSessionCreated: () => void;
}

/**
 * The crawl chooser shown when a scanned pub belongs to more than one
 * crawl. Has no cancel path — Sheet renders it non-dismissible, matching
 * the original popup, which had no close button either.
 */
const WelcomeSheet: React.FC<WelcomeSheetProps> = ({ isOpen, onSessionCreated }) => {
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pub, setPub] = useState<PubDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragDeltaX, setDragDeltaX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const selectedCrawl: CrawlDetail | null = useMemo(() => {
    if (!pub || pub.crawls.length === 0) return null;
    return pub.crawls[carouselIndex] ?? pub.crawls[0];
  }, [pub, carouselIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchPub = async () => {
      const pubId = getQueryParam('pubId');
      if (!pubId) {
        setError('Missing pubId in URL');
        setIsLoading(false);
        return;
      }
      try {
        const resp = await fetch(API_URLS.PUB(pubId));
        if (!resp.ok) throw new Error(`Failed to fetch pub: ${resp.status}`);
        const data: PubDetails = await resp.json();
        setPub(data);
        setIsLoading(false);
      } catch (e) {
        console.error(e);
        setError('Failed to load pub details. Please try again.');
        setIsLoading(false);
      }
    };
    fetchPub();
  }, [isOpen]);

  const handlePrev = () => {
    if (!pub) return;
    setCarouselIndex(i => (i - 1 + pub.crawls.length) % pub.crawls.length);
  };

  const handleNext = () => {
    if (!pub) return;
    setCarouselIndex(i => (i + 1) % pub.crawls.length);
  };

  // Touch/drag handlers for mobile swipe
  const onTouchStart = (clientX: number) => {
    setDragStartX(clientX);
    setIsDragging(true);
  };

  const onTouchMove = (clientX: number) => {
    if (dragStartX === null) return;
    setDragDeltaX(clientX - dragStartX);
  };

  const onTouchEnd = () => {
    if (!pub) {
      setDragStartX(null);
      setDragDeltaX(0);
      setIsDragging(false);
      return;
    }
    const threshold = 50; // px
    if (dragDeltaX > threshold) {
      handlePrev();
    } else if (dragDeltaX < -threshold) {
      handleNext();
    }
    setDragStartX(null);
    setDragDeltaX(0);
    setIsDragging(false);
  };

  const handleStartCrawl = async () => {
    if (!selectedCrawl) return;
    setIsCreatingSession(true);
    setError(null);
    try {
      const session = await SessionService.createSession(selectedCrawl.id);
      SessionService.setSessionId(session.id);
      onSessionCreated();
    } catch (err) {
      setError('Failed to create session. Please try again.');
      console.error('Error creating session:', err);
    } finally {
      setIsCreatingSession(false);
    }
  };

  return (
    <Sheet
      isOpen={isOpen}
      onClose={() => {}}
      dismissible={false}
      title={pub ? `Welcome to ${pub.name}!` : 'Loading pub…'}
      testId="welcome-popup"
    >
      {isLoading && <p className="welcome-sheet-loading">Loading crawls…</p>}
      {!isLoading && pub && pub.crawls.length > 0 && (
        <div className="welcome-sheet-carousel">
          <div className="welcome-sheet-carousel-header">
            <button className="welcome-sheet-nav" onClick={handlePrev} aria-label="Previous crawl">
              <ChevronLeftIcon />
            </button>
            <div
              className={`welcome-sheet-viewport ${isDragging ? 'dragging' : ''}`}
              onMouseDown={e => onTouchStart(e.clientX)}
              onMouseMove={e => isDragging && onTouchMove(e.clientX)}
              onMouseUp={onTouchEnd}
              onMouseLeave={() => isDragging && onTouchEnd()}
              onTouchStart={e => onTouchStart(e.touches[0].clientX)}
              onTouchMove={e => onTouchMove(e.touches[0].clientX)}
              onTouchEnd={onTouchEnd}
            >
              <div
                className="welcome-sheet-track"
                style={{
                  transform: `translateX(calc(${-carouselIndex * 100}% + ${dragDeltaX}px))`,
                }}
              >
                {pub.crawls.map(crawl => (
                  <div key={crawl.id} className="welcome-sheet-card">
                    <h4 data-testid="crawl-option-name">{crawl.name}</h4>
                    <Markdown className="welcome-sheet-card-description">
                      {crawl.description}
                    </Markdown>
                    <ul className="welcome-sheet-card-pubs">
                      {crawl.pubsOnCrawl?.map((crawlPub, index) => (
                        <li key={index}>{crawlPub.name}</li>
                      )) || <li>Loading pubs…</li>}
                    </ul>
                    {crawl.averageDurationHours !== 0 && (
                      <p className="welcome-sheet-card-stat">
                        Average duration: {crawl.averageDurationHours} hours
                      </p>
                    )}
                    <button
                      className="welcome-sheet-start"
                      onClick={handleStartCrawl}
                      disabled={isCreatingSession}
                    >
                      {isCreatingSession ? 'Starting…' : "Let's drink!"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <button className="welcome-sheet-nav" onClick={handleNext} aria-label="Next crawl">
              <ChevronRightIcon />
            </button>
          </div>
          <div className="welcome-sheet-dots">
            {pub.crawls.map((_, idx) => (
              <button
                key={idx}
                aria-label={`Select crawl ${idx + 1}`}
                className={idx === carouselIndex ? 'welcome-sheet-dot active' : 'welcome-sheet-dot'}
                onClick={() => setCarouselIndex(idx)}
              />
            ))}
          </div>
        </div>
      )}

      {error && <p className="welcome-sheet-error">{error}</p>}

      <p className="welcome-sheet-cookie-notice">
        This website uses cookies to work. By continuing to play you accept our use of these
        required cookies.
      </p>
    </Sheet>
  );
};

export default WelcomeSheet;
